// @ts-ignore
import { SingleColumnCongifSchema } from '@fastre/core/schemas/columnConfig'
import { MyUserMembershipSchema } from '@fastre/core/schemas/userMembership'
import { createClient } from '@openauthjs/openauth/client'
import { API } from 'api'
import Loading from 'components/Loading'
import LogRocket from 'logrocket'
import { prop } from 'ramda'
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

const client = createClient({
    clientID: 'web',
    issuer: import.meta.env.VITE_APP_AUTH_URL,
})

export type OrgListUserData = {
    userId: string
    firstName: string
    lastName: string
    email: string
    orgData: {
        [orgId: string]: MyUserMembershipSchema & {
            permissions: MyUserMembershipSchema['rolePermissions']
            fundDisbursementFormConfigured?: boolean
            state: string
            customColumnsConfig: SingleColumnCongifSchema[]

            orgName: string
            orgLogo: string
            xeroConnected: boolean
            zohoConnected: boolean
            annatureConnected: boolean
            reiConnected: boolean
        }
    }
}

const OrgListDataContext = createContext<{
    orgListUserData: OrgListUserData
    refreshOrgListUserData: () => void
}>(null as any)
export const useOrgListData = () => useContext(OrgListDataContext)

export type UserData = {
    userId: string
    firstName: string
    lastName: string
    email: string
    superUser: boolean
    orgId: string
    salesAssistant: boolean
    reiConnected: boolean
} & OrgListUserData['orgData'][string]

export const UserDataContext = createContext<{
    user: UserData
    refreshUser: () => void
}>(null as any)
export const useUserData = () => useContext(UserDataContext)

interface AuthContextType {
    userId?: string
    loaded: boolean
    loggedIn: boolean
    logout: () => void
    login: () => Promise<void>
    getToken: () => Promise<string | undefined>
    token: string
}

const AuthContext = createContext({} as AuthContextType)

export function AuthProvider({ children }: { children: ReactNode }) {
    const initializing = useRef(true)
    const [loaded, setLoaded] = useState(false)
    const [loggedIn, setLoggedIn] = useState(false)
    const token = useRef<string | undefined>(undefined)
    const [userData, setUserData] = useState<any>()
    const [searchParams] = useSearchParams()

    useEffect(() => {
        const code = searchParams.get('code')
        const state = searchParams.get('state')

        if (!initializing.current) {
            return
        }

        initializing.current = false

        if (code && state) {
            callback(code, state)
            return
        }

        auth()
    }, [])

    // Cleanup challenge on unmount to prevent stale state
    useEffect(() => {
        return () => {
            sessionStorage.removeItem('challenge')
        }
    }, [])

    async function auth() {
        const token = await refreshTokens()

        if (token) {
            await user()
        } else {
            await login()
        }

        setLoaded(true)
    }

    async function refreshTokens() {
        const refresh = localStorage.getItem('refresh')
        if (!refresh) return

        try {
            const next = await client.refresh(refresh, {
                access: token.current,
            })
            if (next.err) {
                console.log('Token refresh failed, clearing auth data and restarting login')
                localStorage.removeItem('refresh')
                token.current = undefined
                login()
                return
            }
            if (!next.tokens) return token.current

            // Update tokens if refresh was successful
            token.current = next.tokens.access
            localStorage.setItem('refresh', next.tokens.refresh)
            return token.current
        } catch (error) {
            console.error('Token refresh error:', error)
            localStorage.removeItem('refresh')
            token.current = undefined
            login()
            return
        }
    }

    async function getToken() {
        const token = await refreshTokens()

        if (!token) {
            await login()
            return
        }

        return token
    }

    async function login(force = false) {
        console.log('login')
        if (!force) {
            //return
        }

        // Clear any existing challenge before starting new auth flow
        sessionStorage.removeItem('challenge')

        const { challenge, url } = await client.authorize(location.origin, 'code', {
            pkce: true,
        })

        // Store challenge with timestamp for timeout handling
        const challengeWithTimestamp = {
            ...challenge,
            timestamp: Date.now(),
        }
        sessionStorage.setItem('challenge', JSON.stringify(challengeWithTimestamp))

        // Set timeout to clear stale challenges (5 minutes)
        setTimeout(
            () => {
                const storedChallenge = sessionStorage.getItem('challenge')
                if (storedChallenge) {
                    const parsed = JSON.parse(storedChallenge)
                    if (parsed.timestamp && Date.now() - parsed.timestamp > 5 * 60 * 1000) {
                        console.log('Clearing expired challenge')
                        sessionStorage.removeItem('challenge')
                    }
                }
            },
            5 * 60 * 1000,
        )

        location.href = url
    }

    async function callback(code: string, state: string) {
        try {
            const challengeData = sessionStorage.getItem('challenge')
            if (!challengeData) {
                console.error(
                    'No challenge found in sessionStorage - user may have switched browsers or cleared storage',
                )
                // Clear any stale auth data and redirect to login
                localStorage.removeItem('refresh')
                token.current = undefined
                await login(true)
                return
            }

            const challenge = JSON.parse(challengeData)
            if (!challenge.state || !challenge.verifier) {
                console.error('Invalid challenge data - missing state or verifier')
                sessionStorage.removeItem('challenge')
                localStorage.removeItem('refresh')
                token.current = undefined
                await login(true)
                return
            }

            // Check if challenge has expired (5 minutes)
            if (challenge.timestamp && Date.now() - challenge.timestamp > 5 * 60 * 1000) {
                console.error('Challenge expired - authentication took too long')
                sessionStorage.removeItem('challenge')
                localStorage.removeItem('refresh')
                token.current = undefined
                await login(true)
                return
            }

            // Validate state parameter matches stored challenge
            if (state !== challenge.state) {
                console.error('State mismatch - possible CSRF attack or expired session')
                sessionStorage.removeItem('challenge')
                localStorage.removeItem('refresh')
                token.current = undefined
                await login(true)
                return
            }

            if (code) {
                const exchanged = await client.exchange(code!, location.origin, challenge.verifier)
                if (!exchanged.err) {
                    token.current = exchanged.tokens?.access
                    localStorage.setItem('refresh', exchanged.tokens.refresh)
                    // Clear the challenge after successful exchange
                    sessionStorage.removeItem('challenge')
                } else {
                    console.error('Token exchange failed:', exchanged.err)
                    // Clear challenge and retry login
                    sessionStorage.removeItem('challenge')
                    await login(true)
                    return
                }
            }
            window.location.replace('/')
        } catch (error) {
            console.error('Auth callback error:', error)
            // Clear all auth data and restart login flow
            sessionStorage.removeItem('challenge')
            localStorage.removeItem('refresh')
            token.current = undefined
            await login(true)
        }
    }

    async function user() {
        try {
            const data = await API.get('/me', {
                headers: {
                    Authorization: `Bearer ${token.current}`,
                },
            }).then(prop('data'))

            if (import.meta.env.PROD && data.email !== 'simon.bleier@gmail.com') {
                LogRocket.init('fastre/fastre')
                LogRocket.identify(data.email, {
                    name: `${data.firstName} ${data.lastName}`,
                    email: data.email,
                    stage: import.meta.env.VITE_APP_STAGE,
                })
            }

            setUserData(data)
            setLoggedIn(true)
        } catch (e) {
            console.log('error', e)
            login()
        }
    }

    function logout() {
        localStorage.removeItem('refresh')
        token.current = undefined

        window.location.replace('/')
    }

    return (
        <AuthContext.Provider
            value={{
                login,
                logout,
                loaded,
                loggedIn,
                getToken,
                token: token.current ?? '',
            }}
        >
            {userData ? (
                <OrgListDataContext.Provider
                    value={{
                        orgListUserData: userData,
                        refreshOrgListUserData: user,
                    }}
                >
                    {children}
                </OrgListDataContext.Provider>
            ) : (
                <Loading />
            )}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    return useContext(AuthContext)
}
